import { useContext } from 'react';
import { Typography } from '@mui/material';
import { UserSessionContext } from 'providers/UserSessionProvider';
import type { ConnectorUptimeSummary } from 'types/swagger';
import { secondsToHoursMinutesSeconds } from 'util/uptime';

import type { PeriodType, TimeSpentByPeriod } from './interfaces';

type HeartbeatDonutLabelsProps = {
  timeSpent: TimeSpentByPeriod;
  periodType: PeriodType;
  isLoading: boolean;
};

type HeartbeatType = {
  type: keyof ConnectorUptimeSummary;
  label: string;
  color: string;
};

const HeartbeatDonutLabels: React.FC<HeartbeatDonutLabelsProps> = ({
  timeSpent,
  periodType,
  isLoading,
}) => {
  const { locale } = useContext(UserSessionContext);

  const heartbeatType: HeartbeatType[] = [
    {
      type: 'Idle',
      label: locale === 'es' ? 'Disponible' : 'Available',
      color: '#FFFFFF',
    },
    {
      type: 'Charging',
      label: locale === 'es' ? 'Ocupado' : 'Occupied',
      color: '#00FF7F',
    },
    {
      type: 'Faulted',
      label: locale === 'es' ? 'Con falla' : 'Faulted',
      color: '#C51462',
    },
    {
      type: 'Offline',
      label: locale === 'es' ? 'Fuera de línea' : 'Offline',
      color: '#808080',
    },
  ];

  const getDisplayTime = (type: keyof ConnectorUptimeSummary) => {
    const timeData = timeSpent && timeSpent[type];

    const totalSeconds = (timeData?.minutes || 0) * 60;
    const { hours, minutes } = secondsToHoursMinutesSeconds(totalSeconds);

    if (isLoading || !timeData) {
      return periodType === 'lastDay' ? `- - mins` : `- - hrs - - mins`;
    }

    const hourText = hours === 1 ? 'hr' : 'hrs';
    const minuteText = minutes === 1 ? 'min' : 'mins';

    if (periodType === 'lastDay') {
      return `${timeData?.minutes || 0} ${minuteText}`;
    }

    return `${hours} ${hourText} ${minutes} ${minuteText}`;
  };

  return (
    <ul>
      {heartbeatType.map((heartbeat) => {
        return (
          <li key={heartbeat.label} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div
              style={{
                height: 10,
                width: 10,
                borderRadius: '50%',
                backgroundColor: heartbeat.color,
              }}
            ></div>
            <Typography
              fontSize={16}
              fontWeight={400}
              style={{ fontVariantNumeric: 'tabular-nums' }}
            >
              {getDisplayTime(heartbeat.type)} - {heartbeat.label}
            </Typography>
          </li>
        );
      })}
    </ul>
  );
};

export default HeartbeatDonutLabels;
